/**
 * Analytics utilities for Google Tag Manager (GTM) and GA4
 * 
 * This module provides helper functions to track events via GTM's dataLayer.
 * All events are sent to GTM, which then forwards them to GA4 and other platforms.
 * 
 * @module analytics
 */

// Extend the Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

/**
 * Initialize the dataLayer if it doesn't exist
 */
const initDataLayer = (): void => {
  if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = [];
  }
};

/**
 * Push an event to the GTM dataLayer
 * @param event - The event object to push
 */
const pushToDataLayer = (event: Record<string, unknown>): void => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(event);
    console.log('[Analytics] Event pushed:', event);
  } else {
    console.warn('[Analytics] dataLayer not available');
  }
};

/**
 * Get current language from localStorage or path
 * @returns The current language code (en, de, fr, etc.)
 */
const getCurrentLanguage = (): string => {
  if (typeof window === 'undefined') return 'en';
  
  // Try to get from localStorage first
  const savedLang = localStorage.getItem('preferred-language');
  if (savedLang) return savedLang;
  
  // Fallback to path detection
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const langCode = pathParts[0];
  if (['de', 'fr', 'ja', 'zh', 'ko'].includes(langCode)) return langCode;
  
  return 'en';
};

/**
 * Track a page view (for SPA navigation)
 * @param pagePath - The page path (e.g., '/accommodations')
 * @param pageTitle - The page title
 */
export const trackPageView = (pagePath: string, pageTitle: string): void => {
  initDataLayer();
  
  // Clear all tracking variables first (prevents data pollution)
  pushToDataLayer({
    // Clear button variables
    button_text: undefined,
    button_location: undefined,
    // Clear modal variables
    modal_type: undefined,
    modal_name: undefined,
    parent_modal: undefined,
    page_location: undefined,
    // Clear booking variables
    room_type: undefined,
    // Clear contact variables
    contact_type: undefined,
    contact_source: undefined,
    event_category: undefined,
    event_label: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    region: undefined,
    destination: undefined,
    booking_method: undefined,
    // Clear form variables
    form_name: undefined,
    form_location: undefined,
    // Clear page variables (will be reset below)
    page_path: undefined,
    page_title: undefined,
    // Note: user_language is preserved across events
  });
  
  // Now push the actual event with clean data
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle,
    page_location: window.location.href,
    user_language: getCurrentLanguage(),
  });
  
  // Notify Microsoft Clarity of virtual page view (SPA support)
  // This ensures Clarity properly segments recordings by page
  if (typeof window !== 'undefined' && (window as any).clarity) {
    try {
      (window as any).clarity('set', 'page', pagePath);
      console.log('[Clarity] Virtual page view tracked:', pagePath);
    } catch (error) {
      console.warn('[Clarity] Failed to track virtual page view:', error);
    }
  }
};

/**
 * Track CTA button clicks
 * @param buttonText - The text on the button
 * @param buttonLocation - Where on the page the button is located
 * @param pagePath - Current page path
 */
export const trackCTAClick = (
  buttonText: string,
  buttonLocation?: string,
  pagePath?: string
): void => {
  initDataLayer();
  const currentLanguage = getCurrentLanguage();
  
  // Clear all button/modal-related variables first (prevents data pollution)
  pushToDataLayer({
    // Clear button tracking variables (will be reset below)
    button_text: undefined,
    button_location: undefined,
    // Clear modal variables
    modal_type: undefined,
    modal_name: undefined,
    parent_modal: undefined,
    page_location: undefined,
    // Clear booking variables
    room_type: undefined,
    // Clear contact variables
    contact_type: undefined,
    contact_source: undefined,
    event_category: undefined,
    event_label: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    region: undefined,
    destination: undefined,
    booking_method: undefined,
  });
  
  // Now push the actual event with clean data
  pushToDataLayer({
    event: 'cta_click',
    button_text: buttonText,
    button_location: buttonLocation || 'unknown',
    page_path: pagePath || window.location.pathname,
    user_language: currentLanguage,
  });
};

/**
 * Track booking intent (when user clicks booking-related buttons)
 * @param roomType - The type of room (if applicable)
 * @param buttonText - The text on the button
 * @param buttonLocation - Where the button is located on the page
 */
export const trackBookingIntent = (
  roomType?: string,
  buttonText?: string,
  buttonLocation?: string
): void => {
  initDataLayer();
  const currentLanguage = getCurrentLanguage();
  
  // Clear all non-booking variables first (prevents data pollution)
  pushToDataLayer({
    // Clear modal variables
    modal_type: undefined,
    modal_name: undefined,
    parent_modal: undefined,
    page_location: undefined,
    // Clear contact variables
    contact_type: undefined,
    contact_source: undefined,
    event_category: undefined,
    event_label: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    region: undefined,
    destination: undefined,
    booking_method: undefined,
    // Clear button/booking variables (will be reset below)
    button_text: undefined,
    button_location: undefined,
    room_type: undefined,
  });
  
  // Now push the actual event with clean data
  pushToDataLayer({
    event: 'booking_intent',
    room_type: roomType || 'general',
    button_text: buttonText || 'Book Now',
    button_location: buttonLocation || 'unknown',
    page_path: window.location.pathname,
    user_language: currentLanguage,
  });
};

/**
 * Track form submissions
 * @param formName - The name/type of the form
 * @param formLocation - Where the form is located
 */
export const trackFormSubmit = (
  formName: string,
  formLocation?: string
): void => {
  initDataLayer();
  
  // Clear all tracking variables first (prevents data pollution)
  pushToDataLayer({
    // Clear button variables
    button_text: undefined,
    button_location: undefined,
    // Clear modal variables
    modal_type: undefined,
    modal_name: undefined,
    parent_modal: undefined,
    page_location: undefined,
    // Clear booking variables
    room_type: undefined,
    // Clear contact variables
    contact_type: undefined,
    contact_source: undefined,
    event_category: undefined,
    event_label: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    region: undefined,
    destination: undefined,
    booking_method: undefined,
    // Clear form variables (will be reset below)
    form_name: undefined,
    form_location: undefined,
  });
  
  // Now push the actual event with clean data
  pushToDataLayer({
    event: 'form_submit',
    form_name: formName,
    form_location: formLocation || window.location.pathname,
  });
};

/**
 * Track video engagement
 * @param videoTitle - The title of the video
 * @param videoAction - The action taken (play, pause, complete)
 * @param videoPercent - The percentage watched (if applicable)
 */
export const trackVideoEngagement = (
  videoTitle: string,
  videoAction: 'play' | 'pause' | 'complete',
  videoPercent?: number
): void => {
  initDataLayer();
  
  // Clear all tracking variables first (prevents data pollution)
  pushToDataLayer({
    // Clear button variables
    button_text: undefined,
    button_location: undefined,
    // Clear modal variables
    modal_type: undefined,
    modal_name: undefined,
    parent_modal: undefined,
    page_location: undefined,
    // Clear booking variables
    room_type: undefined,
    // Clear contact variables
    contact_type: undefined,
    contact_source: undefined,
    event_category: undefined,
    event_label: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    region: undefined,
    destination: undefined,
    booking_method: undefined,
    // Clear form variables
    form_name: undefined,
    form_location: undefined,
    // Clear video variables (will be reset below)
    video_title: undefined,
    video_action: undefined,
    video_percent: undefined,
  });
  
  // Now push the actual event with clean data
  pushToDataLayer({
    event: 'video_engagement',
    video_title: videoTitle,
    video_action: videoAction,
    video_percent: videoPercent || 0,
    page_path: window.location.pathname,
  });
};

/**
 * Track outbound link clicks
 * @param linkUrl - The URL being clicked
 * @param linkText - The text of the link
 */
export const trackOutboundClick = (
  linkUrl: string,
  linkText?: string
): void => {
  initDataLayer();
  
  // Clear all tracking variables first (prevents data pollution)
  pushToDataLayer({
    // Clear button variables
    button_text: undefined,
    button_location: undefined,
    // Clear modal variables
    modal_type: undefined,
    modal_name: undefined,
    parent_modal: undefined,
    page_location: undefined,
    // Clear booking variables
    room_type: undefined,
    // Clear contact variables
    contact_type: undefined,
    contact_source: undefined,
    event_category: undefined,
    event_label: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    region: undefined,
    destination: undefined,
    booking_method: undefined,
    // Clear form variables
    form_name: undefined,
    form_location: undefined,
    // Clear link variables (will be reset below)
    link_url: undefined,
    link_text: undefined,
  });
  
  // Now push the actual event with clean data
  pushToDataLayer({
    event: 'outbound_click',
    link_url: linkUrl,
    link_text: linkText || linkUrl,
    page_path: window.location.pathname,
  });
};

/**
 * Track language changes
 * @param fromLanguage - The previous language
 * @param toLanguage - The new language
 */
export const trackLanguageChange = (
  fromLanguage: string,
  toLanguage: string
): void => {
  initDataLayer();
  
  // Clear all tracking variables first (prevents data pollution)
  pushToDataLayer({
    // Clear button variables
    button_text: undefined,
    button_location: undefined,
    // Clear modal variables
    modal_type: undefined,
    modal_name: undefined,
    parent_modal: undefined,
    page_location: undefined,
    // Clear booking variables
    room_type: undefined,
    // Clear contact variables
    contact_type: undefined,
    contact_source: undefined,
    event_category: undefined,
    event_label: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    region: undefined,
    destination: undefined,
    booking_method: undefined,
    // Clear form variables
    form_name: undefined,
    form_location: undefined,
    // Clear language variables (will be reset below)
    from_language: undefined,
    to_language: undefined,
  });
  
  // Now push the actual event with clean data
  pushToDataLayer({
    event: 'language_change',
    from_language: fromLanguage,
    to_language: toLanguage,
    page_path: window.location.pathname,
    user_language: toLanguage, // Set to new language
  });
};

/**
 * Track scroll depth milestones
 * @param percentScrolled - The percentage of the page scrolled
 */
export const trackScrollDepth = (percentScrolled: number): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'scroll_depth',
    percent_scrolled: percentScrolled,
    page_path: window.location.pathname,
  });
};

// ===================================
// ENHANCED ECOMMERCE (FOR FUTURE USE WITH EZEE)
// ===================================

/**
 * Track when user views a room/accommodation
 * @param roomId - The room ID
 * @param roomName - The room name
 * @param roomPrice - The room price
 * @param roomCategory - The room category
 */
export const trackViewItem = (
  roomId: string,
  roomName: string,
  roomPrice: number,
  roomCategory: string
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'view_item',
    ecommerce: {
      currency: 'CHF',
      value: roomPrice,
      items: [
        {
          item_id: roomId,
          item_name: roomName,
          item_category: roomCategory,
          price: roomPrice,
          quantity: 1,
        },
      ],
    },
  });
};

/**
 * Track when user adds room to cart (begins booking process)
 * @param roomId - The room ID
 * @param roomName - The room name
 * @param roomPrice - The room price
 * @param roomCategory - The room category
 */
export const trackAddToCart = (
  roomId: string,
  roomName: string,
  roomPrice: number,
  roomCategory: string
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'add_to_cart',
    ecommerce: {
      currency: 'CHF',
      value: roomPrice,
      items: [
        {
          item_id: roomId,
          item_name: roomName,
          item_category: roomCategory,
          price: roomPrice,
          quantity: 1,
        },
      ],
    },
  });
};

/**
 * Track when user begins checkout
 * @param roomId - The room ID
 * @param roomName - The room name
 * @param roomPrice - The room price
 * @param roomCategory - The room category
 */
export const trackBeginCheckout = (
  roomId: string,
  roomName: string,
  roomPrice: number,
  roomCategory: string
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'CHF',
      value: roomPrice,
      items: [
        {
          item_id: roomId,
          item_name: roomName,
          item_category: roomCategory,
          price: roomPrice,
          quantity: 1,
        },
      ],
    },
  });
};

/**
 * Track successful booking/purchase
 * @param transactionId - The booking/transaction ID
 * @param totalValue - The total booking value
 * @param tax - Tax amount (if applicable)
 * @param items - Array of booked items
 */
export const trackPurchase = (
  transactionId: string,
  totalValue: number,
  tax: number,
  items: Array<{
    item_id: string;
    item_name: string;
    item_category: string;
    price: number;
    quantity: number;
  }>
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'purchase',
    ecommerce: {
      transaction_id: transactionId,
      currency: 'CHF',
      value: totalValue,
      tax: tax,
      items: items,
    },
  });
};

/**
 * Track custom event
 * @param eventName - The name of the custom event
 * @param eventParams - Additional parameters for the event
 */
export const trackCustomEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
): void => {
  initDataLayer();
  pushToDataLayer({
    event: eventName,
    ...eventParams,
    page_path: window.location.pathname,
  });
};

/**
 * Track activity modal open
 */
export const trackActivityModalOpen = (
  activityId: string,
  activityName: string,
  activityCategory: string,
  buttonLocation: string, // Reuses existing button_location variable (consistent with other modals)
  dayPassIncluded: boolean,
  isComingSoon: boolean
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'activity_modal_open',
    activity_id: activityId,
    activity_name: activityName,
    activity_category: activityCategory,
    button_location: buttonLocation, // Reuses existing variable (consistent with other modals)
    day_pass_included: dayPassIncluded,
    is_coming_soon: isComingSoon,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track activity modal close
 */
export const trackActivityModalClose = (
  activityId: string,
  activityName: string,
  closeMethod: 'button' | 'backdrop',
  timeSpentSeconds: number
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'activity_modal_close',
    activity_id: activityId,
    activity_name: activityName,
    close_method: closeMethod,
    time_spent_seconds: timeSpentSeconds,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track activity learn more link click
 */
export const trackActivityLearnMore = (
  activityId: string,
  activityName: string,
  linkType: 'internal' | 'external' | 'anchor',
  linkUrl: string
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'activity_learn_more_click',
    activity_id: activityId,
    activity_name: activityName,
    link_type: linkType,
    link_url: linkUrl,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track activity takeaway link click
 */
export const trackActivityTakeaway = (
  activityId: string,
  activityName: string,
  linkUrl: string
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'activity_takeaway_click',
    activity_id: activityId,
    activity_name: activityName,
    link_url: linkUrl,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track activity contact method click
 * Uses pattern: activity_contact_{actionType} (e.g., activity_contact_phone)
 * @param activityId - The activity ID
 * @param activityName - The activity name
 * @param contactType - The type of contact method
 * @param contactValue - The contact value (phone number, email, or WhatsApp number)
 * @param contactSource - Where the contact was initiated (default: 'activity_modal')
 */
export const trackActivityContact = (
  activityId: string,
  activityName: string,
  contactType: 'phone' | 'email' | 'whatsapp',
  contactValue: string,
  contactSource: string = 'activity_modal'
): void => {
  initDataLayer();
  
  // Clear all contact-related variables first
  pushToDataLayer({
    contact_type: undefined,
    contact_source: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    activity_id: undefined,
    activity_name: undefined,
  });
  
  // Build event data
  const eventData: Record<string, unknown> = {
    event: `activity_contact_${contactType}`,
    activity_id: activityId,
    activity_name: activityName,
    contact_type: contactType,
    contact_source: contactSource,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  };
  
  // Add contact method value
  if (contactType === 'phone') {
    eventData.phone_number = contactValue;
  } else if (contactType === 'email') {
    eventData.email_address = contactValue;
  } else if (contactType === 'whatsapp') {
    eventData.whatsapp_number = contactValue;
  }
  
  pushToDataLayer(eventData);
};

/**
 * Track day pass tray open
 * Event name includes source: day_pass_tray_open_{source}
 * Uses day_pass_* prefix to avoid conflicts with future spa_service_* variables
 * @param source - Source of tray open: 'activity_modal', 'cta_section', 'hero_section', 'pricing_section', 'tray_button', 'concierge_contact'
 * @param activityId - Optional activity ID (if opened from activity modal)
 * @param activityName - Optional activity name (if opened from activity modal)
 */
export const trackDayPassTrayOpen = (
  source: string,
  activityId?: string,
  activityName?: string
): void => {
  initDataLayer();
  
  // Normalize source for event name (replace spaces/special chars with underscores, lowercase)
  const normalizedSource = (source || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  
  pushToDataLayer({
    event: `day_pass_tray_open_${normalizedSource}`, // Source in event name for regex matching
    day_pass_source: source, // Keep for backwards compatibility/context
    activity_id: activityId,
    activity_name: activityName,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track day pass tray close
 * Event name includes source: day_pass_tray_close_{source}
 * Uses day_pass_* prefix to avoid conflicts with future spa_service_* variables
 * @param source - Source where tray was opened from (e.g., 'activity_modal', 'pricing_section')
 * @param currentView - Current view when tray closed
 * @param timeSpentSeconds - Time spent in tray
 * @param guestCount - Number of guests
 * @param hasUpgrades - Whether any upgrades were selected
 * @param formStarted - Whether form was started
 */
export const trackDayPassTrayClose = (
  source: string | null,
  currentView: string,
  timeSpentSeconds: number,
  guestCount: number,
  hasUpgrades: boolean,
  formStarted: boolean
): void => {
  initDataLayer();
  
  // Normalize source for event name (replace spaces/special chars with underscores, lowercase)
  const normalizedSource = (source || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  
  pushToDataLayer({
    event: `day_pass_tray_close_${normalizedSource}`, // Source in event name for regex matching
    day_pass_source: source || 'unknown', // Keep for backwards compatibility/context
    day_pass_current_view: currentView, // Prefixed to avoid conflicts
    day_pass_time_spent_seconds: timeSpentSeconds, // Prefixed
    day_pass_guest_count: guestCount, // Prefixed to avoid conflicts with spa_service_item_count
    day_pass_has_upgrades: hasUpgrades, // Prefixed
    day_pass_form_started: formStarted, // Prefixed
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track guest count change in day pass tray
 * Uses day_pass_* prefix to avoid conflicts with future spa_service_* variables
 */
export const trackDayPassGuestCountChange = (
  action: 'increment' | 'decrement',
  previousCount: number,
  newCount: number,
  currentView: string
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'day_pass_guest_count_change',
    day_pass_action: action, // Prefixed
    day_pass_previous_count: previousCount, // Prefixed
    day_pass_new_count: newCount, // Prefixed
    day_pass_current_view: currentView, // Prefixed
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track view navigation in day pass tray
 * Uses day_pass_* prefix to avoid conflicts with future spa_service_* variables
 */
export const trackDayPassViewNavigation = (
  fromView: string,
  toView: string,
  guestCount: number,
  hasUpgrades: boolean
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'day_pass_view_navigation',
    day_pass_from_view: fromView, // Prefixed
    day_pass_to_view: toView, // Prefixed
    day_pass_guest_count: guestCount, // Prefixed
    day_pass_has_upgrades: hasUpgrades, // Prefixed
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track massage upgrade selection in day pass tray
 * Uses day_pass_* prefix to avoid conflicts with future spa_service_* variables
 */
export const trackDayPassMassageUpgrade = (
  action: 'select' | 'clear',
  guestIndex: number,
  treatmentId?: string,
  treatmentName?: string,
  treatmentPrice?: number,
  upgradePrice?: number,
  guestCount: number,
  totalUpgradePrice: number
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'day_pass_massage_upgrade',
    day_pass_action: action, // Prefixed
    day_pass_guest_index: guestIndex, // Prefixed
    day_pass_treatment_id: treatmentId, // Prefixed
    day_pass_treatment_name: treatmentName, // Prefixed
    day_pass_treatment_price: treatmentPrice, // Prefixed
    day_pass_upgrade_price: upgradePrice, // Prefixed
    day_pass_guest_count: guestCount, // Prefixed
    day_pass_total_upgrade_price: totalUpgradePrice, // Prefixed
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track day pass form start
 * Uses day_pass_* prefix to avoid conflicts with future spa_service_* variables
 */
export const trackDayPassFormStart = (
  guestCount: number,
  hasUpgrades: boolean,
  totalPrice: number,
  basePrice: number,
  upgradePrice: number
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'day_pass_form_start',
    day_pass_guest_count: guestCount, // Prefixed
    day_pass_has_upgrades: hasUpgrades, // Prefixed
    day_pass_total_price: totalPrice, // Prefixed to avoid conflicts with spa_service_total_price
    day_pass_base_price: basePrice, // Prefixed
    day_pass_upgrade_price: upgradePrice, // Prefixed
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track day pass form submit
 * Uses day_pass_* prefix to avoid conflicts with future spa_service_* variables
 */
export const trackDayPassFormSubmit = (
  guestCount: number,
  hasUpgrades: boolean,
  totalPrice: number,
  basePrice: number,
  upgradePrice: number,
  preferredTime: string,
  hasSpecialRequests: boolean,
  submitStatus: 'success' | 'error'
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'day_pass_form_submit',
    day_pass_guest_count: guestCount, // Prefixed
    day_pass_has_upgrades: hasUpgrades, // Prefixed
    day_pass_total_price: totalPrice, // Prefixed to avoid conflicts with spa_service_total_price
    day_pass_base_price: basePrice, // Prefixed
    day_pass_upgrade_price: upgradePrice, // Prefixed
    day_pass_preferred_time: preferredTime, // Prefixed to avoid conflicts with spa_service_preferred_time
    day_pass_has_special_requests: hasSpecialRequests, // Prefixed
    day_pass_submit_status: submitStatus, // Prefixed
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

// ===================================
// SPA TRACKING FUNCTIONS
// ===================================

/**
 * Normalize source string for event names
 * Replaces spaces/special chars with underscores, lowercase
 */
const normalizeSource = (source: string): string => {
  return (source || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

/**
 * Track spa tray open
 * Event name includes source: spa_tray_open_{source}
 * Uses spa_tray_* prefix to avoid conflicts with day_pass_* variables
 * @param source - Source of tray open: 'massage_carousel', 'massage_grid', 'sauna_section', 'pelvipower_section', 'spa_hero', 'tray_button', 'faq_section'
 * @param serviceId - Optional service ID if opened from selector
 * @param serviceName - Optional service name if opened from selector
 */
export const trackSpaTrayOpen = (
  source: string,
  serviceId?: string,
  serviceName?: string
): void => {
  initDataLayer();
  
  const normalizedSource = normalizeSource(source);
  
  pushToDataLayer({
    event: `spa_tray_open_${normalizedSource}`, // Source in event name for regex matching
    spa_tray_source: source, // Human-readable source
    spa_service_id: serviceId,
    spa_service_name: serviceName,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa tray close
 * Event name includes source: spa_tray_close_{source}
 * Uses spa_tray_* prefix to avoid conflicts with day_pass_* variables
 * @param source - Source where tray was opened from
 * @param timeSpentSeconds - Time spent in tray
 * @param currentView - Current view when closed ('selection' | 'catalog')
 * @param formStarted - Whether form was started
 * @param itemCount - Number of items in tray
 * @param totalPrice - Total price in CHF
 */
export const trackSpaTrayClose = (
  source: string | null,
  timeSpentSeconds: number,
  currentView: 'selection' | 'catalog',
  formStarted: boolean,
  itemCount: number,
  totalPrice: number
): void => {
  initDataLayer();
  
  const normalizedSource = normalizeSource(source || 'unknown');
  
  pushToDataLayer({
    event: `spa_tray_close_${normalizedSource}`, // Source in event name for regex matching
    spa_tray_source: source || 'unknown', // Human-readable source
    spa_tray_time_spent_seconds: timeSpentSeconds,
    spa_tray_current_view: currentView,
    spa_tray_form_started: formStarted,
    spa_tray_item_count: itemCount,
    spa_tray_total_price: totalPrice,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa tray view navigation
 * Uses spa_tray_* prefix to avoid conflicts
 * @param fromView - View navigating from ('selection' | 'catalog')
 * @param toView - View navigating to ('selection' | 'catalog')
 * @param itemCount - Current number of items in tray
 */
export const trackSpaTrayViewNavigation = (
  fromView: 'selection' | 'catalog',
  toView: 'selection' | 'catalog',
  itemCount: number
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'spa_tray_view_navigation',
    spa_tray_from_view: fromView,
    spa_tray_to_view: toView,
    spa_tray_item_count: itemCount,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa tray quantity change
 * Uses spa_tray_* prefix to avoid conflicts
 * @param action - 'increment' | 'decrement'
 * @param serviceId - Service ID (e.g., 'classic-massage-60')
 * @param serviceName - Service name
 * @param previousQty - Previous quantity
 * @param newQty - New quantity
 * @param currentView - Current view ('selection' | 'catalog')
 */
export const trackSpaTrayQuantityChange = (
  action: 'increment' | 'decrement',
  serviceId: string,
  serviceName: string,
  previousQty: number,
  newQty: number,
  currentView: 'selection' | 'catalog'
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'spa_tray_quantity_change',
    spa_tray_action: action,
    spa_tray_service_id: serviceId,
    spa_tray_service_name: serviceName,
    spa_tray_previous_quantity: previousQty,
    spa_tray_new_quantity: newQty,
    spa_tray_current_view: currentView,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa tray item removal
 * Uses spa_tray_* prefix to avoid conflicts
 * @param serviceId - Service ID
 * @param serviceName - Service name
 * @param itemCount - Item count after removal
 */
export const trackSpaTrayItemRemove = (
  serviceId: string,
  serviceName: string,
  itemCount: number
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'spa_tray_item_remove',
    spa_tray_service_id: serviceId,
    spa_tray_service_name: serviceName,
    spa_tray_item_count: itemCount,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa tray form start
 * Uses spa_tray_* prefix to avoid conflicts
 * @param itemCount - Number of items in tray
 * @param totalPrice - Total price in CHF
 */
export const trackSpaTrayFormStart = (
  itemCount: number,
  totalPrice: number
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'spa_tray_form_start',
    spa_tray_item_count: itemCount,
    spa_tray_total_price: totalPrice,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa tray form submit
 * Uses spa_tray_* prefix to avoid conflicts
 * @param itemCount - Number of items in tray
 * @param totalPrice - Total price in CHF
 * @param submitStatus - 'success' | 'error'
 */
export const trackSpaTrayFormSubmit = (
  itemCount: number,
  totalPrice: number,
  submitStatus: 'success' | 'error'
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'spa_tray_form_submit',
    spa_tray_item_count: itemCount,
    spa_tray_total_price: totalPrice,
    spa_tray_submit_status: submitStatus,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa selector open
 * Event name includes source: spa_selector_open_{source}
 * Uses spa_selector_* prefix to avoid conflicts
 * @param source - Source where selector was opened: 'massage_carousel', 'massage_grid', 'sauna_section', 'pelvipower_section'
 * @param serviceId - Service ID
 * @param serviceName - Service name
 * @param serviceCategory - Service category ('massage' | 'sauna' | 'pelvipower')
 */
export const trackSpaSelectorOpen = (
  source: string,
  serviceId: string,
  serviceName: string,
  serviceCategory: 'massage' | 'sauna' | 'pelvipower'
): void => {
  initDataLayer();
  
  const normalizedSource = normalizeSource(source);
  
  pushToDataLayer({
    event: `spa_selector_open_${normalizedSource}`, // Source in event name for regex matching
    spa_selector_source: source, // Human-readable source
    spa_service_id: serviceId,
    spa_service_name: serviceName,
    spa_service_category: serviceCategory,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa selector close
 * Event name includes source: spa_selector_close_{source}
 * Uses spa_selector_* prefix to avoid conflicts
 * @param source - Source where selector was opened
 * @param serviceId - Service ID
 * @param serviceName - Service name
 * @param hasSelections - Whether any quantities were selected
 * @param timeSpentSeconds - Optional: time selector was open
 * @param selectionCount - Optional: number of duration/quantity selections made
 */
export const trackSpaSelectorClose = (
  source: string,
  serviceId: string,
  serviceName: string,
  hasSelections: boolean,
  timeSpentSeconds?: number,
  selectionCount?: number
): void => {
  initDataLayer();
  
  const normalizedSource = normalizeSource(source);
  
  const eventData: Record<string, unknown> = {
    event: `spa_selector_close_${normalizedSource}`, // Source in event name for regex matching
    spa_selector_source: source, // Human-readable source
    spa_service_id: serviceId,
    spa_service_name: serviceName,
    spa_selector_has_selections: hasSelections,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  };
  
  if (timeSpentSeconds !== undefined) {
    eventData.spa_selector_time_spent_seconds = timeSpentSeconds;
  }
  
  if (selectionCount !== undefined) {
    eventData.spa_selector_selection_count = selectionCount;
  }
  
  pushToDataLayer(eventData);
};

/**
 * Track spa selector quantity change
 * Uses spa_selector_* prefix to avoid conflicts
 * @param action - 'increment' | 'decrement'
 * @param serviceId - Service ID
 * @param serviceName - Service name
 * @param duration - Duration in minutes (or 'Session' for sauna/pelvipower)
 * @param previousQty - Previous quantity
 * @param newQty - New quantity
 * @param totalPrice - Total price for all selections in this selector
 */
export const trackSpaSelectorQuantityChange = (
  action: 'increment' | 'decrement',
  serviceId: string,
  serviceName: string,
  duration: string,
  previousQty: number,
  newQty: number,
  totalPrice: number
): void => {
  initDataLayer();
  pushToDataLayer({
    event: 'spa_selector_quantity_change',
    spa_selector_action: action,
    spa_service_id: serviceId,
    spa_service_name: serviceName,
    spa_service_duration: duration,
    spa_selector_previous_quantity: previousQty,
    spa_selector_new_quantity: newQty,
    spa_selector_total_price: totalPrice,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  });
};

/**
 * Track spa selector "Add to My List" button click
 * Uses spa_selector_* prefix to avoid conflicts
 * @param source - Source where selector was opened from
 * @param serviceId - Service ID
 * @param serviceName - Service name
 * @param serviceCategory - Service category ('massage' | 'sauna' | 'pelvipower')
 * @param itemCount - Total number of items added (sum of all quantities)
 * @param totalPrice - Total price in CHF
 * @param selections - Array of selections made (duration, quantity, price)
 */
export const trackSpaSelectorAddToList = (
  source: string,
  serviceId: string,
  serviceName: string,
  serviceCategory: 'massage' | 'sauna' | 'pelvipower',
  itemCount: number,
  totalPrice: number,
  selections?: Array<{ duration: string; quantity: number; price: number }>
): void => {
  initDataLayer();
  
  const eventData: Record<string, unknown> = {
    event: 'spa_selector_add_to_list',
    spa_selector_source: source,
    spa_service_id: serviceId,
    spa_service_name: serviceName,
    spa_service_category: serviceCategory,
    spa_selector_item_count: itemCount,
    spa_selector_total_price: totalPrice,
    page_path: window.location.pathname,
    user_language: getCurrentLanguage(),
  };
  
  // Add selections array if provided
  if (selections && selections.length > 0) {
    eventData.spa_selector_selections = selections;
  }
  
  pushToDataLayer(eventData);
};

// Initialize dataLayer on module load
if (typeof window !== 'undefined') {
  initDataLayer();
}


